import React, { useState, useEffect, useCallback } from 'react';
import Header from './ui/Header';
import ContactsView from './views/ContactsView';
import ScanView from './views/ScanView';
import openclawService from '../services/openclawService';
import api from '../api';
import { triggerHaptic } from '../utils/haptics';
import { LANGUAGES } from '../constants/config';
import { Sparkles, Contact, ScanLine, Brain, Ghost, Settings, ChevronRight, Cpu, Bell, Globe, Loader2, Save, Trash2, ShieldAlert } from 'lucide-react';
import { NoAutoFillInput, NoAutoFillTextarea } from './ui/NoAutoFillInput';

const SidebarMenu = ({ isOpen, onClose, userId, settings, onUpdateSettings, t, avatarImage, onAvatarUpload, userName, aiAssistantName, onAiNameChange, agentConfig, setAgentConfig, availableAgents, onScanUser, onChatWithFriend, onShowAvatarCard, onLogout }) => {
  const [currentView, setCurrentView] = useState('main');
  const [editingAiName, setEditingAiName] = useState('');
  const [savingAiName, setSavingAiName] = useState(false);
  const avatarInputRef = React.useRef(null);

  useEffect(() => { if (!isOpen) { setCurrentView('main'); } else { setEditingAiName(aiAssistantName || ''); } }, [isOpen, aiAssistantName]);

  const menuItems = [
    { id: 'avatar-card', label: '分身名片', icon: ScanLine },
    { id: 'contacts', label: t.contacts, icon: Contact },
    { id: 'scan', label: t.scan, icon: ScanLine },
    { id: 'memory', label: t.memory, icon: Brain },
    { id: 'privacy', label: t.privacy, icon: Ghost }
  ];

  const handleSaveAiName = async () => {
    if (!editingAiName.trim() || savingAiName) return;
    setSavingAiName(true);
    try {
      await openclawService.updateInstance({ name: editingAiName.trim() });
      if (onAiNameChange) onAiNameChange(editingAiName.trim());
      triggerHaptic();
    } catch (err) {
      console.error('Failed to update AI name:', err);
    }
    setSavingAiName(false);
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onAvatarUpload) {
      onAvatarUpload(file);
      triggerHaptic();
    }
  };

  const renderAiAgents = () => (
    <div className="space-y-6 animate-in slide-in-from-right duration-300">
      <Header title="AI Agents" t={t} onBack={() => setCurrentView('main')} />
      <div className="space-y-4">
        {availableAgents?.map(agent => (
          <div key={agent.id} className="p-4 glass-card rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {agent.id === 'browser_agent' && '🌐'}
                  {agent.id === 'calendar_agent' && '📅'}
                  {agent.id === 'file_agent' && '📁'}
                </span>
                <div>
                  <p className="text-white font-bold">{agent.name}</p>
                  <p className="text-xs text-gray-400">{agent.description}</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={agentConfig[agent.id.replace('_agent', '_control')]?.enabled || false}
                  onChange={(e) => {
                    const newConfig = {
                      ...agentConfig,
                      [agent.id.replace('_agent', '_control')]: { enabled: e.target.checked }
                    };
                    setAgentConfig(newConfig);
                    triggerHaptic();
                  }}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 rounded-full p-1 transition-colors ${agentConfig[agent.id.replace('_agent', '_control')]?.enabled ? 'bg-[var(--neon-primary)]' : 'bg-gray-700/50'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${agentConfig[agent.id.replace('_agent', '_control')]?.enabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {agent.capabilities.map(cap => (
                <span key={cap} className="text-xs px-2 py-1 rounded bg-white/10 text-gray-300">
                  {cap}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAvatarSettings = () => (
    <div className="space-y-6 animate-in slide-in-from-right duration-300">
      <Header title="AI分身设置" t={t} onBack={() => setCurrentView('main')} />
      <div className="p-5 glass-card rounded-2xl border-l-4 border-l-[var(--neon-primary)]">
        <p className="text-sm text-cyan-300 font-bold mb-4 flex items-center gap-2">
          <Sparkles size={18} /> AI分身名字
        </p>
        <p className="text-xs text-gray-400 mb-4">自定义你的AI分身名字，问"你是谁"时会用这个名字回答</p>
        <NoAutoFillInput
          type="text"
          inputMode="text"
          value={editingAiName}
          onChange={(e) => { if (e.target.value.length <= 20) setEditingAiName(e.target.value); }}
          placeholder={aiAssistantName || '我是你的AI分身'}
          className="w-full glass-input rounded-xl px-4 py-3 text-sm mb-3 outline-none focus:ring-1 focus:ring-[var(--neon-primary)]"
          maxLength={20}
          name="ai-name"
          id="ai-name"
        />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-500 font-mono">{editingAiName.length}/20</span>
          <button
            onClick={handleSaveAiName}
            disabled={!editingAiName.trim() || savingAiName}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
              editingAiName.trim() && !savingAiName
                ? 'bg-[var(--neon-primary)] text-black hover:opacity-90'
                : 'bg-white/10 text-gray-500 cursor-not-allowed'
            }`}
          >
            {savingAiName ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {savingAiName ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
      <div className="p-5 glass-card rounded-2xl">
        <p className="text-xs text-gray-400 mb-2">当前AI分身名字</p>
        <p className="text-lg font-bold text-white glow-text-cyan">{aiAssistantName || '我是你的AI分身'}</p>
      </div>
    </div>
  );

  const [memories, setMemories] = useState([]);
  const [memoryStats, setMemoryStats] = useState({ total: 0, byType: {} });
  const [loadingMemories, setLoadingMemories] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [showAddMemory, setShowAddMemory] = useState(false);
  const [newMemoryType, setNewMemoryType] = useState('fact');

  const memoryTypes = [
    { id: 'fact', label: '事实', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { id: 'preference', label: '偏好', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { id: 'habit', label: '习惯', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { id: 'goal', label: '目标', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { id: 'emotion', label: '情感', color: 'bg-pink-500/20 text-pink-400 border-pink-500/30' }
  ];

  const loadMemories = useCallback(async () => {
    if (!userId) return;
    setLoadingMemories(true);
    try {
      const [memoriesData, statsData] = await Promise.all([
        api.getMemories(userId, { limit: 20 }),
        api.getMemoryStats(userId)
      ]);
      setMemories(memoriesData || []);
      setMemoryStats(statsData?.stats || { total: 0, byType: {} });
    } catch (err) {
      console.error('[SidebarMenu] Failed to load memories:', err);
    } finally {
      setLoadingMemories(false);
    }
  }, [userId]);

  useEffect(() => {
    if (currentView === 'memory') {
      loadMemories();
    }
  }, [currentView, loadMemories]);

  const handleAddMemory = async () => {
    if (!newMemoryContent.trim() || !userId) return;
    try {
      await api.createMemory(userId, {
        type: newMemoryType,
        content: newMemoryContent.trim()
      });
      setNewMemoryContent('');
      setShowAddMemory(false);
      loadMemories();
      triggerHaptic();
    } catch (err) {
      console.error('[SidebarMenu] Failed to add memory:', err);
    }
  };

  const handleDeleteMemory = async (memoryId) => {
    try {
      await api.deleteMemory(memoryId);
      loadMemories();
      triggerHaptic();
    } catch (err) {
      console.error('[SidebarMenu] Failed to delete memory:', err);
    }
  };

  const handleClearAllMemories = async () => {
    if (!window.confirm('确定要清空所有记忆吗？此操作不可恢复。')) return;
    try {
      await api.clearAllMemories(userId);
      loadMemories();
      triggerHaptic();
    } catch (err) {
      console.error('[SidebarMenu] Failed to clear memories:', err);
    }
  };

  const renderMemory = () => (
    <div className="space-y-4 animate-in slide-in-from-right duration-300 h-full flex flex-col">
      <Header title={t.memory} t={t} onBack={() => setCurrentView('main')} />
      
      <div className="p-4 glass-card rounded-2xl border-l-4 border-l-[var(--neon-primary)]">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-cyan-300 font-bold flex items-center gap-2">
            <Brain size={18} /> 记忆统计
          </p>
          <span className="text-xs text-gray-400">共 {memoryStats.total} 条</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {memoryTypes.map(type => {
            const count = memoryStats.byType[type.id] || 0;
            if (count === 0) return null;
            return (
              <div key={type.id} className={`px-2.5 py-1 ${type.color} text-xs font-bold rounded-lg`}>
                {type.label}: {count}
              </div>
            );
          })}
          {memoryStats.total === 0 && (
            <span className="text-xs text-gray-500">暂无记忆数据</span>
          )}
        </div>
      </div>

      {!showAddMemory ? (
        <button
          onClick={() => setShowAddMemory(true)}
          className="w-full py-3 bg-[var(--neon-primary)]/10 border border-[var(--neon-primary)]/30 text-[var(--neon-primary)] text-sm font-bold rounded-xl hover:bg-[var(--neon-primary)]/20 transition-colors flex items-center justify-center gap-2"
        >
          <span className="text-lg">+</span> 添加记忆
        </button>
      ) : (
        <div className="p-4 glass-card rounded-2xl space-y-3">
          <div className="flex gap-2 flex-wrap">
            {memoryTypes.map(type => (
              <button
                key={type.id}
                onClick={() => setNewMemoryType(type.id)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                  newMemoryType === type.id ? type.color : 'bg-white/5 text-gray-400'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
          <NoAutoFillTextarea
            value={newMemoryContent}
            onChange={(e) => setNewMemoryContent(e.target.value)}
            placeholder="输入要记忆的内容..."
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-gray-500 outline-none focus:border-[var(--neon-primary)] resize-none"
            rows={3}
            name="memory-content"
            id="memory-content"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddMemory}
              disabled={!newMemoryContent.trim()}
              className="flex-1 py-2 bg-[var(--neon-primary)] text-black text-sm font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              保存
            </button>
            <button
              onClick={() => { setShowAddMemory(false); setNewMemoryContent(''); }}
              className="px-4 py-2 bg-white/5 text-gray-400 text-sm font-bold rounded-xl hover:bg-white/10 transition-colors"
            >
              取消
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {loadingMemories ? (
          <div className="text-center py-8 text-gray-500 text-sm">加载中...</div>
        ) : memories.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            暂无记忆<br />
            <span className="text-xs">添加一些记忆帮助AI更好地了解你</span>
          </div>
        ) : (
          memories.map(memory => {
            const typeInfo = memoryTypes.find(t => t.id === memory.type) || memoryTypes[0];
            return (
              <div key={memory.id} className="p-3 glass-card rounded-xl group">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {new Date(memory.createdAt).toLocaleDateString('zh-CN')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200 break-words">{memory.content}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteMemory(memory.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {memories.length > 0 && (
        <button
          onClick={handleClearAllMemories}
          className="w-full py-3 border border-red-500/30 text-red-400 text-sm font-bold hover:bg-red-500/10 rounded-xl flex items-center justify-center gap-2 transition-colors"
        >
          <Trash2 size={16} />
          清空所有记忆
        </button>
      )}
    </div>
  );

  const renderPrivacy = () => (
    <div className="space-y-6 animate-in slide-in-from-right duration-300">
      <Header title={t.privacy} t={t} onBack={() => setCurrentView('main')} />
      <div className="flex items-center justify-between p-5 glass-card rounded-2xl">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Ghost size={18} className="text-[var(--neon-primary)]" />
            <span className="font-bold text-base text-white">{t.ghostMode}</span>
          </div>
          <p className="text-xs text-gray-400">{t.ghostDesc}</p>
        </div>
        <div className="w-12 h-6 bg-gray-700/50 rounded-full p-1">
          <div className="w-4 h-4 bg-white rounded-full shadow-md"></div>
        </div>
      </div>
      <button className="w-full py-4 mt-8 bg-red-900/20 border border-red-500/50 text-red-500 font-bold text-sm rounded-2xl flex items-center justify-center gap-2">
        <ShieldAlert size={18} /> {t.deleteData}
      </button>
    </div>
  );

  const toggleLanguage = useCallback(() => {
    onUpdateSettings(prev => {
      const idx = LANGUAGES.findIndex(l => l.code === prev.language);
      const nextLang = LANGUAGES[(idx + 1) % LANGUAGES.length].code;
      localStorage.setItem('settings_language', nextLang);
      return { ...prev, language: nextLang };
    });
    triggerHaptic();
  }, [onUpdateSettings]);

  const renderMain = () => {
    const currentLangLabel = LANGUAGES.find(l => l.code === settings.language)?.label || '中文';

    return (
      <div className="animate-in fade-in duration-300">
        <input
          type="file"
          accept="image/*"
          ref={avatarInputRef}
          onChange={handleAvatarChange}
          className="hidden"
        />
        <div 
          className="w-24 h-24 mb-6 mx-auto cursor-pointer group"
          onClick={handleAvatarClick}
        >
          <div className="w-24 h-24 rounded-full p-1.5 bg-gradient-to-tr from-[var(--neon-primary)] to-[var(--neon-secondary)] shadow-[0_0_50px_rgba(0,240,255,0.4)] relative overflow-hidden">
            <img src={avatarImage} alt="Avatar" className="w-full h-full object-cover rounded-full bg-black" />
            <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-xs text-white font-medium">更换头像</span>
            </div>
          </div>
        </div>

        <h2 className="text-3xl font-black text-white mb-2 tracking-tight text-center text-glow">{userName}</h2>
        <p className="text-xs text-[var(--neon-primary)] font-mono tracking-widest mb-10 text-center">ID: {userId || '-----'}</p>

        <div className="space-y-1">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-3 ml-2"><Settings size={12} /> {t.general}</p>
          {menuItems.map(item => (
            <div key={item.id} onClick={() => {
              triggerHaptic();
              if (item.id === 'avatar-card') {
                onShowAvatarCard && onShowAvatarCard();
              } else {
                setCurrentView(item.id);
              }
            }} className="py-3 px-2 hover:bg-white/5 active:scale-98 transition-all cursor-pointer flex items-center justify-between group rounded-xl">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-white/5 rounded-xl text-[var(--neon-primary)] shadow-sm"><item.icon size={20} /></div>
                <span className="text-sm font-bold text-gray-200 group-hover:text-white">{item.label}</span>
              </div>
              <ChevronRight size={18} className="text-gray-600 group-hover:text-[var(--neon-primary)]" />
            </div>
          ))}
        </div>

        <div className="space-y-2 mt-8">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 ml-2"><Cpu size={12} /> {t.systemPref}</p>

          <div
            className="flex items-center justify-between py-3 px-2 hover:bg-white/5 active:scale-98 transition-all cursor-pointer rounded-xl"
            onClick={() => { triggerHaptic(); onUpdateSettings(prev => ({ ...prev, notifications: !prev.notifications })); }}
          >
            <div className="flex items-center gap-3 text-sm text-gray-200 font-bold">
              <div className="p-2.5 bg-white/5 rounded-xl text-gray-400 shadow-sm"><Bell size={20} /></div>
              <span>{t.notifications}</span>
            </div>
            <div className={`w-12 h-7 rounded-full p-1 transition-colors ${settings.notifications ? 'bg-[var(--neon-primary)]' : 'bg-gray-700/50'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${settings.notifications ? 'translate-x-5' : 'translate-x-0'}`}></div>
            </div>
          </div>

          <div
            className="flex items-center justify-between py-3 px-2 hover:bg-white/5 active:scale-98 transition-all cursor-pointer rounded-xl"
            onClick={() => { triggerHaptic(); setCurrentView('ai-agents'); }}
          >
            <div className="flex items-center gap-3 text-sm text-gray-200 font-bold">
              <div className="p-2.5 bg-white/5 rounded-xl text-[var(--neon-primary)] shadow-sm"><Settings size={20} /></div>
              <span>AI Agents</span>
            </div>
            <ChevronRight size={18} className="text-gray-600" />
          </div>

          <div className="flex items-center justify-between py-3 px-2 hover:bg-white/5 active:scale-98 transition-all cursor-pointer rounded-xl" onClick={() => { triggerHaptic(); toggleLanguage(); }}>
            <div className="flex items-center gap-3 text-sm text-gray-200 font-bold">
              <div className="p-2.5 bg-white/5 rounded-xl text-gray-400 shadow-sm"><Globe size={20} /></div>
              <span>{t.language}</span>
            </div>
            <div className="text-xs font-mono text-[var(--neon-primary)] border border-[var(--neon-primary)]/30 px-3 py-1.5 rounded-lg bg-[var(--neon-primary)]/10 min-w-[80px] text-center font-bold">
              {currentLangLabel}
            </div>
          </div>
        </div>

        {onLogout && (
          <button
            onClick={() => { triggerHaptic(); onLogout(); onClose(); }}
            className="w-full py-3 mt-8 mb-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-400 font-medium flex items-center justify-center gap-2 hover:bg-red-500/30"
          >
            <span>{t.logout || '退出登录'}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-xl animate-fade-in" onClick={() => { triggerHaptic(); onClose(); }} />
      )}
      {isOpen && (
        <div className="fixed top-0 left-0 h-full w-[85%] max-w-xs bg-[#050510]/95 backdrop-blur-2xl border-r border-white/10 z-[70] safe-top shadow-[20px_0_60px_rgba(0,0,0,0.8)] animate-slide-in">
          <div className="p-6 h-full flex flex-col">
            <div className="flex-1 overflow-y-auto no-scrollbar">
              {currentView === 'main' && renderMain()}
              {currentView === 'ai-agents' && renderAiAgents()}
              {currentView === 'avatar-settings' && renderAvatarSettings()}
              {currentView === 'contacts' && <ContactsView t={t} onBack={() => setCurrentView('main')} userId={userId} onChatWithFriend={onChatWithFriend} />}
              {currentView === 'scan' && <ScanView t={t} onBack={() => setCurrentView('main')} onScanUser={onScanUser} />}
            {currentView === 'memory' && renderMemory()}
            {currentView === 'privacy' && renderPrivacy()}
          </div>
        </div>
        </div>
      )}
    </>
  );
};

export default SidebarMenu;
